import {
    useImportedAccounts,
    useAccountingMigration,
    useFinalizeAccountsMapping,
} from "@api/accountingIntegration.api";
import { Entity } from "@common/types/entity";
import { MapImportedAccountsSpreadsheet } from "@components/accountingMigration/MapImportedAccountsSpreadsheet";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import React, { useCallback, useEffect, useMemo } from "react";
import styles from "./BalanceSheetImporter.module.scss";

interface Props {
    entity: Entity;
    onSave: () => void;
}

export const MapAccountsStep: React.FC<Props> = ({ entity, onSave }) => {
    const { workspaceId } = useScope();
    const { toast } = useToaster();

    const { data: importedAccountsData, refetch: refetchAccounts } =
        useImportedAccounts(workspaceId ?? null, entity.id, {
            orderBy: "name",
        });
    const importedAccounts = importedAccountsData?.accounts;
    const accountGroups = importedAccountsData?.accountGroups;

    const shouldRefetchUntilMappingFinished = useMemo(() => {
        if (!importedAccounts) {
            return false;
        }
        return (
            importedAccounts.length > 0 &&
            importedAccounts.some(
                (account) => account.pendingAccountId === null,
            )
        );
    }, [importedAccounts]);

    const { data: accountingMigration } = useAccountingMigration(
        workspaceId ?? null,
        entity.id,
        { refetchUntilMappingFinished: shouldRefetchUntilMappingFinished },
    );

    const isAccountsMappingRunning =
        !!accountingMigration && !accountingMigration.accountsMappedAt;

    useEffect(() => {
        if (!isAccountsMappingRunning) {
            refetchAccounts();
        }
    }, [refetchAccounts, isAccountsMappingRunning]);

    const finalizeMutation = useFinalizeAccountsMapping(entity.id);

    const handleAcceptMapping = useCallback(() => {
        finalizeMutation.mutate(
            {},
            {
                onSuccess: () => {
                    onSave();
                },
                onError: (error) => {
                    toast(
                        `Failed to finalize account mapping: ${error instanceof Error ? error.message : "Please try again."}`,
                    );
                },
            },
        );
    }, [finalizeMutation, onSave, toast]);

    const notMappedCount = useMemo(() => {
        if (!importedAccounts) {
            return 0;
        }
        return importedAccounts.filter(
            (a) => !a.pendingAccountId && !(a.createNewAccount && a.kickType),
        ).length;
    }, [importedAccounts]);

    if (!importedAccounts || !workspaceId) {
        return null;
    }

    return (
        <>
            <div className={styles.mapAccountsSection}>
                <p className={styles.dateSectionDescription}>
                    For each account, map to an existing Kick account, or create
                    a new one.
                </p>
            </div>
            <MapImportedAccountsSpreadsheet
                accounts={importedAccounts}
                accountGroups={accountGroups}
                entity={entity}
                workspaceId={workspaceId}
                entityId={entity.id}
                isAccountsMappingRunning={isAccountsMappingRunning}
                providerHeader={<span>Imported account</span>}
            />
            <CommandIsland sticky alignEnd>
                {accountingMigration &&
                    !accountingMigration.accountsMappingFinalizedAt && (
                        <ButtonWithLoader
                            size="md"
                            loading={
                                finalizeMutation.isPending ||
                                finalizeMutation.isSuccess
                            }
                            variant="primary"
                            hotKey="mod+enter"
                            hotKeyDisplayPosition="inline"
                            hotKeyOptions={{ description: "Accept mapping" }}
                            onClick={handleAcceptMapping}
                            disabled={notMappedCount > 0}
                        >
                            Accept mapping
                        </ButtonWithLoader>
                    )}
            </CommandIsland>
        </>
    );
};
